import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Zap, Loader2, Copy, CheckCircle2, UserX } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import QRCode from 'qrcode';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';

interface AnonymousZapButtonProps {
  target: NostrEvent;
  onInvoiceGenerated?: () => void;
  onPaymentSuccess?: () => void;
}

export function AnonymousZapButton({ target, onInvoiceGenerated, onPaymentSuccess }: AnonymousZapButtonProps) {
  const { toast } = useToast();
  const { data: author } = useAuthor(target.pubkey);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('21000');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [invoice, setInvoice] = useState<string | null>(null);
  const [verifyUrl, setVerifyUrl] = useState<string | null>(null);
  const [isPaid, setIsPaid] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Generate QR code when invoice is available
  useEffect(() => {
    if (invoice) {
      QRCode.toDataURL(invoice, { 
        width: 350,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' }
      }).then(setQrCodeUrl).catch(console.error);
      onInvoiceGenerated?.();
    } else {
      setQrCodeUrl('');
    }
  }, [invoice, onInvoiceGenerated]);

  // Poll for payment status
  useEffect(() => {
    if (!verifyUrl || isPaid) return;

    const checkPayment = async () => {
      try {
        const response = await fetch(verifyUrl);
        const data = await response.json();
        
        // Check if payment is settled
        if (data.settled === true || data.status === 'settled' || data.paid === true) {
          setIsPaid(true);
          
          // Clear polling interval
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }

          // Show success message
          toast({
            title: 'Payment received!',
            description: 'Thank you for your support! The goal will update shortly.',
          });

          // Trigger callback and close dialog after a moment
          setTimeout(() => {
            onPaymentSuccess?.();
            setOpen(false);
          }, 2000);
        }
      } catch (error) {
        console.error('Payment verification error:', error);
      }
    };

    // Start polling every 2 seconds
    pollIntervalRef.current = setInterval(checkPayment, 2000);

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [verifyUrl, isPaid, toast, onPaymentSuccess]);

  const handleGenerateInvoice = async () => {
    const sats = parseInt(amount);
    if (isNaN(sats) || sats <= 0) return;

    // Get Lightning address from user's metadata
    const lightningAddress = author?.metadata?.lud16 || author?.metadata?.lud06;
    
    if (!lightningAddress) {
      toast({
        title: 'No Lightning Address',
        description: 'This user does not have a Lightning address configured.',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Parse Lightning address
      let lnurlEndpoint: string;
      
      if (lightningAddress.startsWith('lnurl')) {
        // lud06 - bech32 encoded LNURL
        // Decode and use directly (this is more complex, so we'll handle lud16 primarily)
        throw new Error('bech32 LNURL not yet supported, please use lud16 (email-style) address');
      } else {
        // lud16 - email-style address (user@domain.com)
        const [username, domain] = lightningAddress.split('@');
        if (!username || !domain) {
          throw new Error('Invalid Lightning address format');
        }
        lnurlEndpoint = `https://${domain}/.well-known/lnurlp/${username}`;
      }

      // Fetch LNURL pay data
      const lnurlRes = await fetch(lnurlEndpoint);
      const lnurlData = await lnurlRes.json();

      if (lnurlData.status === 'ERROR') {
        throw new Error(lnurlData.reason || 'LNURL service error');
      }

      // Request invoice
      const amountMsats = sats * 1000;
      const callbackUrl = new URL(lnurlData.callback);
      callbackUrl.searchParams.set('amount', amountMsats.toString());

      const invoiceRes = await fetch(callbackUrl.toString());
      const invoiceData = await invoiceRes.json();

      if (invoiceData.status === 'ERROR') {
        throw new Error(invoiceData.reason || 'Failed to get invoice');
      }

      if (!invoiceData.pr) {
        throw new Error('No invoice returned');
      }

      setInvoice(invoiceData.pr);
      
      // Store verify URL if available for payment polling
      if (invoiceData.verify) {
        setVerifyUrl(invoiceData.verify);
      } else if (invoiceData.successAction?.url) {
        setVerifyUrl(invoiceData.successAction.url);
      }
      
    } catch (error) {
      console.error('Invoice generation error:', error);
      toast({
        title: 'Failed to generate invoice',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Cleanup state when dialog closes
      setInvoice(null);
      setVerifyUrl(null);
      setIsPaid(false);
      setCopied(false);
      
      // Clear polling interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  };

  const handleCopyInvoice = async () => {
    if (invoice) {
      await navigator.clipboard.writeText(invoice);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetInvoice = () => {
    setInvoice(null);
    setVerifyUrl(null);
    setIsPaid(false);
    
    // Clear polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const quickAmounts = [1000, 5000, 10000, 21000, 50000, 100000];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button 
          variant="outline"
          className="w-full border-2 border-orange-500/30 text-orange-600 hover:bg-orange-50 font-bold h-14 text-base"
        >
          <UserX className="h-5 w-5 mr-2" />
          Donate Anonymously
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg bg-white border-2 border-orange-500/20 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-3xl font-black text-black">Anonymous Support</DialogTitle>
          <p className="text-base text-gray-700 font-semibold mt-2">
            Support this campaign anonymously via Lightning invoice
          </p>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {invoice ? (
            <motion.div
              key="invoice"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className={`border-2 p-4 rounded-xl ${
                isPaid 
                  ? 'bg-green-50 border-green-500' 
                  : 'bg-orange-50 border-orange-500/30'
              }`}>
                <p className="text-sm font-bold text-black mb-1">
                  {isPaid ? '✅ Payment Received!' : '💜 Anonymous Contribution'}
                </p>
                <p className="text-sm text-gray-700">
                  {isPaid 
                    ? 'Thank you for your support! The goal will update shortly.'
                    : 'Your payment will be anonymous'
                  }
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl border-2 border-orange-500/20">
                {qrCodeUrl && (
                  <motion.img 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    src={qrCodeUrl} 
                    alt="Lightning Invoice QR Code" 
                    className="w-full max-w-[350px] mx-auto rounded-xl" 
                  />
                )}
              </div>
              
              <div className="space-y-3">
                <Label className="text-black font-black text-lg">Lightning Invoice</Label>
                <Textarea
                  value={invoice}
                  readOnly
                  rows={4}
                  className="font-mono text-xs bg-orange-50/50 border-2 border-orange-500/30 text-black"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCopyInvoice}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold h-14"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-5 w-5 mr-2" />
                      Copy Invoice
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={resetInvoice}
                  className="flex-1 border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-bold h-14"
                >
                  Back
                </Button>
              </div>

              <p className="text-sm text-center text-gray-600 font-medium">
                Scan the QR code or copy the invoice to pay with any Lightning wallet
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              <div className="bg-orange-50 border-2 border-orange-500/30 p-5 rounded-xl">
                <p className="text-base font-black text-black mb-2">💜 Anonymous Donation</p>
                <p className="text-base text-gray-700 font-medium">
                  Your contribution will be visible but attributed as "Anonymous" in the campaign's supporter list.
                </p>
              </div>

              {/* Quick Amounts */}
              <div className="space-y-4">
                <Label className="text-black font-black text-xl">Select Amount</Label>
                <div className="grid grid-cols-3 gap-3">
                  {quickAmounts.map(amt => (
                    <Button
                      key={amt}
                      type="button"
                      variant={amount === amt.toString() ? 'default' : 'outline'}
                      onClick={() => setAmount(amt.toString())}
                      className={amount === amt.toString() 
                        ? 'bg-orange-500 text-white font-bold border-2 border-orange-600 h-14 text-lg shadow-lg'
                        : 'border-2 border-orange-500/30 text-orange-600 hover:bg-orange-50 font-bold h-14 text-lg'
                      }
                    >
                      {(amt / 1000).toLocaleString()}k
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="space-y-3">
                <Label htmlFor="amount" className="text-black font-black text-xl">
                  Custom Amount (sats)
                </Label>
                <Input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="bg-orange-50/50 border-2 border-orange-500/30 text-black text-3xl font-black h-16 focus:ring-4 focus:ring-orange-500/20 text-center"
                />
              </div>

              {/* Generate Invoice Button */}
              <Button
                onClick={handleGenerateInvoice}
                disabled={isGenerating || parseInt(amount) <= 0}
                className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white h-20 text-2xl font-black shadow-2xl shadow-orange-500/40"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-3 h-7 w-7 animate-spin" />
                    Generating Invoice...
                  </>
                ) : (
                  <>
                    <Zap className="mr-3 h-7 w-7" fill="currentColor" />
                    Generate Invoice
                  </>
                )}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
